import { addDays, addHours, isSameSecond, setHours, startOfHour } from "date-fns";
import type React from "react";
import { useState } from "react";

import { DatePicker } from "@/components/DatePicker/DatePicker";
import { Drawer } from "@/components/Drawer/Drawer";
import { FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { CalendarCheck01Icon } from "@/components/Icons/CalendarCheck01Icon";
import { CalendarDateIcon } from "@/components/Icons/CalendarDateIcon";
import { CalendarIcon } from "@/components/Icons/CalendarIcon";
import { ClockCheckIcon } from "@/components/Icons/ClockCheckIcon";
import { RadioCard } from "@/components/Radio/RadioCard";
import { Button } from "@/design-system/Button";
import { useTranslation } from "@/translations";

interface AdminCreatePostFormScheduleSelectorProps {
  selectedDate: Date | undefined;
  onSelectDate: (date: Date | undefined) => void;
  maxDate?: Date;
}
export function AdminCreatePostFormScheduleSelector({
  selectedDate,
  onSelectDate,
  maxDate,
}: AdminCreatePostFormScheduleSelectorProps): React.ReactNode {
  const [drawerSession, setDrawerSession] = useState({ isOpened: false, id: 0 });
  const { t, i18n } = useTranslation();
  const selectedDateTime = selectedDate?.getTime();
  const selectedDateKey = `${drawerSession.id}:${selectedDateTime ?? "post-now"}`;

  return (
    <>
      <Button
        variant="subtle"
        data-testid="schedule-selector-btn"
        onClick={() => setDrawerSession((session) => ({ isOpened: true, id: session.id + 1 }))}
        icon={<ClockCheckIcon />}
      >
        {selectedDate
          ? t("page.admin-create-post.form.btn.scheduled-for", {
              date: formatDate(i18n, "date", selectedDate),
            })
          : t("page.admin-create-post.form.btn.schedule")}
      </Button>
      <Drawer.Root
        title={t("page.admin-create-post.scheduled-for-selector.drawer.title")}
        isOpened={drawerSession.isOpened}
        onOpenChange={(isOpened) => setDrawerSession((session) => ({ ...session, isOpened }))}
        data-testid="schedule-selector-drawer"
      >
        <ScheduleOptions
          key={selectedDateKey}
          selectedDate={selectedDate}
          onSelectDate={onSelectDate}
          maxDate={maxDate}
        />
      </Drawer.Root>
    </>
  );
}

function ScheduleOptions({
  selectedDate,
  onSelectDate,
  maxDate,
}: AdminCreatePostFormScheduleSelectorProps): React.ReactNode {
  const [activeDate, setActiveDate] = useState(selectedDate);
  const [dateNow] = useState(() => new Date());
  const { t, i18n } = useTranslation();

  const getUpcomingMorningDateTime = () => {
    if (dateNow.getHours() >= 9) {
      return startOfHour(addDays(dateNow, 1).setHours(9));
    } else {
      return startOfHour(setHours(dateNow, 9));
    }
  };

  const getNextWeekDateTime = () => {
    return startOfHour(addHours(addDays(dateNow, 7), 1));
  };

  const dateUpcomingMorning = getUpcomingMorningDateTime();
  const dateNextWeek = getNextWeekDateTime();
  const isAfterMaxDate = (date: Date | undefined) => Boolean(date && maxDate && date >= maxDate);
  const [option, setOption] = useState(() => {
    if (!selectedDate) {
      return "postNow";
    }
    if (isSameSecond(selectedDate, dateUpcomingMorning)) {
      return "postNextMorning";
    }
    if (isSameSecond(selectedDate, dateNextWeek)) {
      return "postNextWeek";
    }

    return "postCustomDateTime";
  });
  const selectPreset = (value: string, date: Date | undefined) => {
    setOption(value);
    setActiveDate(date);
  };
  const isCustomDateTimeSelected = option === "postCustomDateTime";
  const isInvalidDate = option !== "postNow" && (!activeDate || activeDate <= dateNow || isAfterMaxDate(activeDate));

  return (
    <>
      <Drawer.Body>
        <FormRadioCardGroup layout="vertical">
          <RadioCard
            data-testid="schedule-selector-post-now-btn"
            icon={<CalendarCheck01Icon />}
            label={t("page.admin-create-post.schedule-selector.drawer.btn.post-now")}
            description={t("page.admin-create-post.schedule-selector.drawer.btn.post-now.description")}
            value="postNow"
            checked={option === "postNow"}
            onChange={() => selectPreset("postNow", undefined)}
            name="scheduleTime"
          />
          <RadioCard
            data-testid="schedule-next-morning-btn"
            icon={<CalendarIcon />}
            label={t("page.admin-create-post.schedule-selector.drawer.btn.post-incoming-morning")}
            description={formatDate(i18n, "date", dateUpcomingMorning)}
            value="postNextMorning"
            checked={option === "postNextMorning"}
            onChange={() => selectPreset("postNextMorning", dateUpcomingMorning)}
            disabled={isAfterMaxDate(dateUpcomingMorning)}
            name="scheduleTime"
          />
          <RadioCard
            data-testid="schedule-next-week-btn"
            icon={<CalendarIcon />}
            label={t("page.admin-create-post.schedule-selector.drawer.btn.post-next-week")}
            description={formatDate(i18n, "datetime", dateNextWeek)}
            value="postNextWeek"
            checked={option === "postNextWeek"}
            onChange={() => selectPreset("postNextWeek", dateNextWeek)}
            disabled={isAfterMaxDate(dateNextWeek)}
            name="scheduleTime"
          />
          <RadioCard
            icon={<CalendarDateIcon />}
            label={t("page.admin-create-post.schedule-selector.drawer.btn.post-custom-datetime")}
            value="postCustomDateTime"
            checked={isCustomDateTimeSelected}
            onChange={() => selectPreset("postCustomDateTime", undefined)}
            name="scheduleTime"
            data-testid="schedule-custom-datetime-btn"
          />
          {isCustomDateTimeSelected && (
            <DatePicker
              type="datetime"
              data-testid="schedule-custom-datetime-picker-input"
              minuteStep={15}
              min={startOfHour(addHours(dateNow, 1))}
              max={maxDate}
              onChange={(date) => setActiveDate(date)}
              value={activeDate}
            />
          )}
        </FormRadioCardGroup>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.Close>
          <Button variant="subtle" tone="primary">
            {t("common.action.cancel")}
          </Button>
        </Drawer.Close>
        <Drawer.Close>
          <Button data-testid="confirm-schedule-btn" disabled={isInvalidDate} onClick={() => onSelectDate(activeDate)}>
            {t("common.action.select")}
          </Button>
        </Drawer.Close>
      </Drawer.Actions>
    </>
  );
}
